import React, { useEffect, useState } from "react";
import "../CSS/AttachmentOpportunities.css";
import { useNavigate } from "react-router-dom";
import OpportunityDetailsModal from "./OpportunityDetailsModal";
import Cookies from "js-cookie";
import logo from "../../assets/icone.png";

/* =========================================================
   API URL
========================================================= */

const API_URL =
  "https://advenwarecareerlink.com/acl/employer/Display_offers.php";

/* =========================================================
   FORMAT POSTED DATE
========================================================= */

const formatPostedDate = (dateString) => {
  if (!dateString) {
    return "Recently posted";
  }

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return dateString;
  }

  const now = new Date();

  const difference =
    now.getTime() - date.getTime();

  const minutes = Math.floor(
    difference / (1000 * 60)
  );

  const hours = Math.floor(
    difference / (1000 * 60 * 60)
  );

  const days = Math.floor(
    difference / (1000 * 60 * 60 * 24)
  );

  if (difference < 0) {
    return "Recently posted";
  }

  if (minutes < 60) {
    if (minutes <= 1) {
      return "Just now";
    }

    return `${minutes} minutes ago`;
  }

  if (hours < 24) {
    if (hours === 1) {
      return "1 hour ago";
    }

    return `${hours} hours ago`;
  }

  if (days < 7) {
    if (days === 1) {
      return "1 day ago";
    }

    return `${days} days ago`;
  }

  return date.toLocaleDateString("en-KE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/* =========================================================
   FORMAT REQUIREMENTS
========================================================= */

const formatRequirements = (requirements) => {
  if (!requirements) {
    return [];
  }

  if (Array.isArray(requirements)) {
    return requirements;
  }

  if (typeof requirements === "string") {
    try {
      const parsed = JSON.parse(requirements);

      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      // Not JSON, continue below
    }

    return requirements
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item !== "");
  }

  return [];
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const AttachmentOpportunities = () => {
  const [opportunities, setOpportunities] = useState([]);

  const [search, setSearch] = useState("");

  const [county, setCounty] =
    useState("All Counties");

  const [category, setCategory] =
    useState("All Categories");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [selectedOpportunity, setSelectedOpportunity] =
    useState(null);

  const [cookies, setCookies] =
    useState("");

  const navigate = useNavigate();

  /* =======================================================
     NAVIGATION
  ======================================================= */

  const handleNavigation = (destination) => {
    navigate(`/${destination}`);
  };

  /* =======================================================
     GET USER COOKIE
  ======================================================= */

  useEffect(() => {
    const username = Cookies.get("userID");

    if (username) {
      setCookies(username);
    }
  }, []);

  /* =======================================================
     FETCH OPPORTUNITIES FROM PHP
  ======================================================= */

  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        setLoading(true);
        setError("");

        console.log(
          "Fetching opportunities from:",
          API_URL
        );

        const response = await fetch(API_URL, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Server returned ${response.status}`
          );
        }

        const data = await response.json();

        console.log(
          "PHP OPPORTUNITIES RESPONSE:",
          data
        );

        /* =================================================
           PHP RETURNS AN ARRAY DIRECTLY

           Example:

           [
             {
               name: "...",
               date: "...",
               job_title: "...",
               attachment_type: "...",
               user_ID: "...",
               industry: "...",
               openings: "...",
               contact: "...",
               website: "...",
               description: "...",
               duration: "...",
               county: "..."
             }
           ]
        ================================================= */

        if (!Array.isArray(data)) {
          throw new Error(
            "Invalid response received from PHP. Expected an array."
          );
        }

        /* =================================================
           CONVERT PHP DATA TO UI DATA
        ================================================= */

        const formattedOpportunities =
          data.map((item, index) => {
            return {
              /* ID */
              id:
                item.user_ID ||
                item.id ||
                index + 1,

              /* ORGANISATION */
              organization:
                item.name ||
                "Unknown Organisation",

              /* JOB TITLE */
              title:
                item.job_title ||
                "Attachment Opportunity",

              /* CATEGORY */
              category:
                item.industry ||
                item.attachment_type ||
                "General",

              /* COUNTY */
              county:
                item.county ||
                "Not specified",

              /* LOCATION */
              location:
                item.county ||
                "Kenya",

              /* ATTACHMENT TYPE */
              attachmentType:
                item.attachment_type ||
                "Attachment",

              /* DURATION */
              duration:
                item.duration ||
                "Not specified",

              /* OPENINGS */
              openings:
                Number(item.openings) || 0,

              /* APPLICATIONS */
              applications:
                Number(item.applications) || 0,

              /* POSTED DATE */
              posted:
                formatPostedDate(item.date),

              /* RAW DATE */
              date:
                item.date || "",

              /* DESCRIPTION */
              description:
                item.description ||
                "No description provided.",

              /* REQUIREMENTS */
              requirements:
                formatRequirements(
                  item.requirements
                ),

              /* RESPONSIBILITIES */
              responsibilities:
                formatRequirements(
                  item.responsibilities
                ),

              /* CONTACT */
              contact:
                item.contact || "",

              /* EMAIL */
              contactEmail:
                item.contact_email ||
                item.email ||
                item.contact ||
                "",

              /* WEBSITE */
              website:
                item.website || "",

              /* USER ID */
              user_ID:
                item.user_ID || "",
            };
          });

        console.log(
          "FORMATTED OPPORTUNITIES:",
          formattedOpportunities
        );

        setOpportunities(
          formattedOpportunities
        );

      } catch (error) {
        console.error(
          "OPPORTUNITIES FETCH ERROR:",
          error
        );

        setError(
          error.message ||
            "Unable to load opportunities."
        );

        setOpportunities([]);

      } finally {
        setLoading(false);
      }
    };

    fetchOpportunities();
  }, []);

  /* =======================================================
     GET UNIQUE COUNTIES
  ======================================================= */

  const counties = [
    "All Counties",
    ...Array.from(
      new Set(
        opportunities
          .map(
            (opportunity) =>
              opportunity.county
          )
          .filter(
            (countyName) =>
              countyName &&
              countyName !==
                "Not specified"
          )
      )
    ),
  ];

  /* =======================================================
     GET UNIQUE CATEGORIES
  ======================================================= */

  const categories = [
    "All Categories",
    ...Array.from(
      new Set(
        opportunities
          .map(
            (opportunity) =>
              opportunity.category
          )
          .filter(
            (categoryName) =>
              categoryName &&
              categoryName !== "General"
          )
      )
    ),
  ];

  /* =======================================================
     FILTER OPPORTUNITIES
  ======================================================= */

  const filteredOpportunities =
    opportunities.filter(
      (opportunity) => {
        const searchText =
          search.toLowerCase().trim();

        const matchesSearch =
          opportunity.title
            ?.toLowerCase()
            .includes(searchText) ||

          opportunity.organization
            ?.toLowerCase()
            .includes(searchText) ||

          opportunity.category
            ?.toLowerCase()
            .includes(searchText) ||

          opportunity.location
            ?.toLowerCase()
            .includes(searchText) ||

          opportunity.county
            ?.toLowerCase()
            .includes(searchText) ||

          opportunity.description
            ?.toLowerCase()
            .includes(searchText);

        const matchesCounty =
          county === "All Counties" ||
          opportunity.county === county;

        const matchesCategory =
          category ===
            "All Categories" ||
          opportunity.category ===
            category;

        return (
          matchesSearch &&
          matchesCounty &&
          matchesCategory
        );
      }
    );

  /* =======================================================
     VIEW OPPORTUNITY
  ======================================================= */

  const handleViewOpportunity = (
    opportunity
  ) => {
    setSelectedOpportunity(opportunity);
  };

  /* =======================================================
     CLOSE MODAL
  ======================================================= */

  const handleCloseModal = () => {
    setSelectedOpportunity(null);
  };

  /* =======================================================
     RETURN UI
  ======================================================= */

  return (
    <div className="opportunities-page">

      {/* =================================================
          NAVBAR
      ================================================= */}

      <header className="opportunities-navbar">

        <div className="opportunities-brand">

            <img
                         src={logo}
                         alt="Advenware Career Link"
                         className="landing-logo-image"
                       />

          <div>
            <strong>
              Advenware
            </strong>

            <span>
              Careere Link
            </span>
          </div>

        </div>

        {!cookies ? (
          <div className="nav-actions">

            <button
              onClick={() =>
                handleNavigation("login")
              }
              className="login-button"
            >
              Login
            </button>

            <button
              onClick={() =>
                handleNavigation(
                  "Create_account"
                )
              }
              className="register-button"
            >
              Create Account
            </button>

          </div>
        ) : null}

      </header>


      {/* =================================================
          HERO
      ================================================= */}

      <section className="opportunities-hero">

        <div className="hero-content">

          <div className="hero-label">
            ATTACHMENT OPPORTUNITIES
          </div>

          <h1>
            Find where your
            <br />
            career begins.
          </h1>

          <p>
            Explore attachment opportunities
            from organisations across Kenya
            and find a placement that matches
            your course.
          </p>

          <div className="hero-stats">

            <div>

              <strong>
                {opportunities.length}+
              </strong>

              <span>
                Opportunities
              </span>

            </div>

            <div>

              <strong>
                {
                  new Set(
                    opportunities.map(
                      (item) =>
                        item.organization
                    )
                  ).size
                }+
              </strong>

              <span>
                Organisations
              </span>

            </div>

            <div>

              <strong>
                {
                  new Set(
                    opportunities.map(
                      (item) =>
                        item.category
                    )
                  ).size
                }+
              </strong>

              <span>
                Categories
              </span>

            </div>

          </div>

        </div>


        {/* =================================================
            HERO DECORATION
        ================================================= */}

        <div className="hero-decoration">

          <div className="floating-card card-one">

            <span>
              ✓
            </span>

            <div>

              <strong>
                Placement found
              </strong>

              <small>
                Software Development
              </small>

            </div>

          </div>


          <div className="floating-card card-two">

            <span>
              ◉
            </span>

            <div>

              <strong>
                New opportunity
              </strong>

              <small>
                Posted today
              </small>

            </div>

          </div>


          <div className="hero-circle">

            <div className="hero-circle-inner">
              AC
            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          SEARCH SECTION
      ================================================= */}

      <section className="search-section">

        <div className="search-heading">

          <div>

            <span>
              OPPORTUNITY DIRECTORY
            </span>

            <h2>
              Available attachments
            </h2>

          </div>

          <p>
            Find the right opportunity
            for your career.
          </p>

        </div>


        <div className="search-container">

          <div className="large-search">

            <span>
              ⌕
            </span>

            <input
              type="text"
              placeholder="Search by job title, organisation, course..."
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
            />

          </div>


          {/* COUNTY FILTER */}

          <select
            value={county}
            onChange={(e) =>
              setCounty(
                e.target.value
              )
            }
          >

            {counties.map(
              (countyName) => (
                <option
                  key={countyName}
                  value={countyName}
                >
                  {countyName}
                </option>
              )
            )}

          </select>


          {/* CATEGORY FILTER */}

          <select
            value={category}
            onChange={(e) =>
              setCategory(
                e.target.value
              )
            }
          >

            {categories.map(
              (categoryName) => (
                <option
                  key={categoryName}
                  value={categoryName}
                >
                  {categoryName}
                </option>
              )
            )}

          </select>


          <button
            className="search-button"
            onClick={() => {
              // Filtering happens automatically
              // when search/filter values change.
            }}
          >
            Search
          </button>

        </div>

      </section>


      {/* =================================================
          OPPORTUNITIES CONTENT
      ================================================= */}

      <main className="opportunities-content">

        <div className="results-header">

          <div>

            <strong>
              {filteredOpportunities.length}
            </strong>

            <span>
              opportunities available
            </span>

          </div>

          <button
            className="sort-button"
            onClick={() => {
              const sorted = [
                ...opportunities,
              ].sort(
                (a, b) =>
                  new Date(b.date || 0) -
                  new Date(a.date || 0)
              );

              setOpportunities(sorted);
            }}
          >
            Most recent ▾
          </button>

        </div>


        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (

          <div className="loading-container">

            <div className="loading-spinner">
            </div>

            <p>
              Finding opportunities...
            </p>

          </div>


        ) : error ? (

          /* =================================================
             ERROR
          ================================================= */

          <div className="no-results">

            <div className="no-results-icon">
              !
            </div>

            <h3>
              Unable to load opportunities
            </h3>

            <p>
              {error}
            </p>

            <button
              className="search-button"
              onClick={() =>
                window.location.reload()
              }
            >
              Try Again
            </button>

          </div>


        ) : filteredOpportunities.length ===
          0 ? (

          /* =================================================
             NO RESULTS
          ================================================= */

          <div className="no-results">

            <div className="no-results-icon">
              ⌕
            </div>

            <h3>
              No opportunities found
            </h3>

            <p>
              Try changing your search
              or filters.
            </p>

          </div>


        ) : (

          /* =================================================
             OPPORTUNITY CARDS
          ================================================= */

          <div className="opportunities-grid">

            {filteredOpportunities.map(
              (opportunity) => (

                <article
                  className="opportunity-card"
                  key={
                    opportunity.id
                  }
                >

                  {/* ======================================
                      TOP
                  ====================================== */}

                  <div className="opportunity-top">

                    <div className="organization-logo">

                      {opportunity.organization
                        ?.charAt(0)
                        ?.toUpperCase()}

                    </div>

                    <div className="posted">

                      {opportunity.posted}

                    </div>

                  </div>


                  {/* ======================================
                      CATEGORY
                  ====================================== */}

                  <div className="opportunity-category">

                    {opportunity.category}

                  </div>


                  {/* ======================================
                      TITLE
                  ====================================== */}

                  <h3>

                    {opportunity.title}

                  </h3>


                  {/* ======================================
                      ORGANISATION
                  ====================================== */}

                  <div className="organization">

                    {opportunity.organization}

                  </div>


                  {/* ======================================
                      DESCRIPTION
                  ====================================== */}

                  <p className="opportunity-description">

                    {opportunity.description}

                  </p>


                  {/* ======================================
                      META
                  ====================================== */}

                  <div className="opportunity-meta">

                    <div>

                      <span>
                        COUNTY LOCATION
                      </span>

                      <strong>
                        ◉{" "}
                        {opportunity.county}
                      </strong>

                    </div>


                    <div>

                      <span>
                        DURATION
                      </span>

                      <strong>
                        ◷{" "}
                        {opportunity.duration}
                      </strong>

                    </div>

                  </div>


                  {/* ======================================
                      REQUIREMENTS
                  ====================================== */}

                  <div className="requirements">

                    <span>
                      ACCEPTING:
                    </span>

                    <div className="requirement-tags">

                      {(
                        opportunity.requirements ||
                        []
                      )
                        .slice(0, 2)
                        .map(
                          (
                            requirement,
                            index
                          ) => (

                            <span
                              key={index}
                            >
                              {requirement}
                            </span>

                          )
                        )}


                      {(
                        opportunity.requirements ||
                        []
                      ).length > 2 && (

                        <span>

                          +
                          {
                            opportunity
                              .requirements
                              .length - 2
                          }

                        </span>

                      )}

                      {(
                        opportunity.requirements ||
                        []
                      ).length === 0 && (

                        <span>
                          Open to suitable students
                        </span>

                      )}

                    </div>

                  </div>


                  {/* ======================================
                      BOTTOM
                  ====================================== */}

                  <div className="opportunity-bottom">

                    <div className="openings">

                      <strong>
                        {
                          opportunity.openings
                        }
                      </strong>

                      <span>
                        openings
                      </span>

                    </div>


                    <button
                      className="view-opportunity"
                      onClick={() =>
                        handleViewOpportunity(
                          opportunity
                        )
                      }
                    >

                      View opportunity

                      <span>
                        →
                      </span>

                    </button>

                  </div>

                </article>

              )
            )}

          </div>

        )}

      </main>


      {/* =================================================
          CTA
      ================================================= */}

      <section className="opportunity-cta">

        <div>

          <span>
            LOOKING FOR AN ATTACHMENT?
          </span>

          <h2>
            Your next opportunity could
            be one click away.
          </h2>

          <p>
            Create your Advenware
            Careere Link account and
            start applying today.
          </p>

        </div>

        <button
          onClick={() =>
            handleNavigation(
              "Create_account"
            )
          }
        >
          Create Student Account →
        </button>

      </section>


      {/* =================================================
          FOOTER
      ================================================= */}

      <footer className="opportunity-footer">

        <div>

          <strong>
            Advenware Careere Link
          </strong>

          <span>
            Connecting students with
            opportunity.
          </span>

        </div>


        <div className="footer-links">

          <a href="#">
            About
          </a>

          <a href="#">
            Opportunities
          </a>

          <a href="#">
            Employers
          </a>

          <a href="#">
            Contact
          </a>

        </div>


        <div>
          © 2026 Advenware
        </div>

      </footer>


      {/* =================================================
          OPPORTUNITY MODAL
      ================================================= */}

      {selectedOpportunity && (

        <OpportunityDetailsModal
          opportunity={
            selectedOpportunity
          }
          onClose={
            handleCloseModal
          }
        />

      )}

    </div>
  );
};

export default AttachmentOpportunities;